// D:\docker-files\11.AI_trade3\3000.dashboard\src\pages\EmailVerificationPage.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Brain, Mail, Lock, ArrowLeft, RefreshCw } from 'lucide-react';
import { userService } from '../services/api';
import { getErrorMessage } from '../utils/helpers';
import toast from 'react-hot-toast';

const EmailVerificationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const emailFromParams = searchParams.get('email');
  
  const [formData, setFormData] = useState({
    email: emailFromParams || '',
    verification_code: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [timeLeft, setTimeLeft] = useState(300); // 5분 = 300초

  // 타이머 효과
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  // 시간 포맷팅
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.email || !formData.verification_code) {
      setError('이메일과 인증번호를 모두 입력해주세요.');
      return;
    }

    if (formData.verification_code.length !== 6) {
      setError('인증번호는 6자리 숫자입니다.');
      return;
    }

    setIsLoading(true);
    try {
      await userService.verifyEmailCode({
        email: formData.email,
        verification_code: formData.verification_code
      });
      
      setSuccess('이메일 인증이 완료되었습니다! 로그인 페이지로 이동합니다.');
      toast.success('이메일 인증 완료!');
      
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      setError(getErrorMessage(error));
      toast.error('인증 실패');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!formData.email) {
      setError('이메일을 입력해주세요.');
      return;
    }

    setIsResending(true);
    setError('');
    
    try {
      await userService.resendVerificationCode({
        email: formData.email
      });
      
      setSuccess('새로운 인증번호가 발송되었습니다.');
      setTimeLeft(300); // 타이머 리셋
      toast.success('인증번호 재발송 완료!');
    } catch (error) {
      setError(getErrorMessage(error));
      toast.error('재발송 실패');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* 헤더 */}
        <div className="text-center">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Mail className="w-10 h-10 text-white" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            이메일 인증
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            이메일로 발송된 6자리 인증번호를 입력해주세요
          </p>
        </div>

        {/* 인증 폼 */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg text-sm">
                {success}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                이메일 주소
              </label>
              <div className="relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  placeholder="your@email.com"
                />
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
            </div>

            <div>
              <label htmlFor="verification_code" className="block text-sm font-medium text-gray-700 mb-2">
                인증번호 (6자리)
              </label>
              <div className="relative">
                <input
                  id="verification_code"
                  name="verification_code"
                  type="text"
                  required
                  maxLength="6"
                  value={formData.verification_code}
                  onChange={handleChange}
                  className="w-full px-3 py-3 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-center text-lg font-mono tracking-wider"
                  placeholder="123456"
                />
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
              {timeLeft > 0 && (
                <p className="text-xs text-gray-500 mt-1 text-center">
                  ⏰ 남은 시간: {formatTime(timeLeft)}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-white bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              ) : (
                <Lock className="w-5 h-5 mr-2" />
              )}
              {isLoading ? '인증 중...' : '이메일 인증하기'}
            </button>
          </form>

          {/* 재발송 및 뒤로가기 */}
          <div className="mt-6 space-y-4">
            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isResending || timeLeft > 240} // 1분 후에 재발송 가능
                className="text-sm text-blue-600 hover:text-blue-500 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center mx-auto"
              >
                {isResending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                    재발송 중...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1" />
                    인증번호를 받지 못했나요?
                  </>
                )}
              </button>
              {timeLeft > 240 && (
                <p className="text-xs text-gray-500 mt-1">
                  {formatTime(timeLeft - 240)} 후에 재발송 가능합니다
                </p>
              )}
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/register')}
                className="text-sm text-gray-600 hover:text-gray-500 transition-colors flex items-center justify-center mx-auto"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                회원가입으로 돌아가기
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;